/**
 * Set the same sharing on every selected document at once.
 *
 * Only what the user ticks is sent. A field that is not ticked keeps what each
 * document has; that matters when 250 documents with different sharing are
 * selected and only the visibility is meant to change.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { RecipientPicker } from '../ui/RecipientPicker/RecipientPicker';
import type { RecipientSelection } from '../ui/RecipientPicker/types';
import { DocumentVisibility, type BulkDocumentPermissionsRequest } from '../../api/data-contracts';

export type BulkPermissionsChange = Omit<BulkDocumentPermissionsRequest, 'document_ids'>;

export interface BulkPermissionsDialogProps {
  open: boolean;
  count: number;
  loading?: boolean;
  onClose: () => void;
  onApply: (change: BulkPermissionsChange) => void;
}

const VISIBILITIES = Object.values(DocumentVisibility);

export const BulkPermissionsDialog: React.FC<BulkPermissionsDialogProps> = ({
  open,
  count,
  loading = false,
  onClose,
  onApply,
}) => {
  const { t } = useTranslation();
  const [changeVisibility, setChangeVisibility] = useState(false);
  const [visibility, setVisibility] = useState<DocumentVisibility>(DocumentVisibility.Private);
  const [changeSharing, setChangeSharing] = useState(false);
  const [sharing, setSharing] = useState<RecipientSelection>({ users: [], groups: [] });

  const nothingChosen = !changeVisibility && !changeSharing;

  const apply = () => {
    const change: BulkPermissionsChange = {};
    if (changeVisibility) { change.visibility = visibility; }
    if (changeSharing) {
      // An empty list clears the sharing; that is the point of ticking it.
      change.shared_with_users = sharing.users;
      change.shared_with_groups = sharing.groups;
    }
    onApply(change);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('documents.bulk.permissionsTitle', { count })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('documents.bulk.permissionsHint')}
          </Typography>

          <FormControlLabel
            control={<Checkbox checked={changeVisibility} onChange={(e) => setChangeVisibility(e.target.checked)} />}
            label={t('documents.bulk.changeVisibility')}
          />
          {changeVisibility && (
            <FormControl fullWidth>
              <InputLabel>{t('documents.permissions.visibility')}</InputLabel>
              <Select
                value={visibility}
                label={t('documents.permissions.visibility')}
                onChange={(e) => setVisibility(e.target.value as DocumentVisibility)}
              >
                {VISIBILITIES.map((v) => (
                  <MenuItem key={v} value={v}>{t(`documents.visibility.${v}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={<Checkbox checked={changeSharing} onChange={(e) => setChangeSharing(e.target.checked)} />}
            label={t('documents.bulk.changeSharing')}
          />
          {changeSharing && (
            <Box>
              <RecipientPicker
                value={sharing}
                onChange={setSharing}
                variant="side-by-side"
                showUsers
                showGroups
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('documents.bulk.sharingReplaces')}
              </Typography>
            </Box>
          )}

          {nothingChosen && (
            <Alert severity="info">{t('documents.bulk.nothingChosen')}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={apply} disabled={nothingChosen || loading}>
          {t('documents.bulk.apply', { count })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkPermissionsDialog;
